import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home, Briefcase, FileText, Plus, X, Calendar, Clock, MapPin,
  Loader2, CheckCircle, AlertTriangle, Zap, ChevronDown
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getPortalToken = () => {
  const token = localStorage.getItem("portal_token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

// ======================== BOOK JOB MODAL ========================

const BookJobModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [form, setForm] = useState({
    service_type: "",
    preferred_date: "",
    preferred_time: "",
    address: "",
    description: "",
    urgency: "normal",
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setSubmitted(false);
      setForm({ service_type: "", preferred_date: "", preferred_time: "", address: "", description: "", urgency: "normal" });
      fetchServices();
      fetchAddresses();
    }
  }, [open]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/service-types`, getPortalToken());
      setServices(res.data || []);
    } catch {
      // Fallback — services list not critical
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/portal/addresses`, getPortalToken());
      setSavedAddresses(res.data?.addresses || []);
    } catch { /* ok */ }
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) {
      toast.error("Please describe the service you need");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portal/service-request`, form, getPortalToken());
      if (res.data.success) {
        setSubmitted(true);
        toast.success("Service request submitted!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onClose();
    navigate("/portal/jobs");
  };

  // Address autocomplete via Google Places
  const searchTimeout = React.useRef(null);
  const handleAddressChange = (value) => {
    setForm({ ...form, address: value });
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length >= 3) {
      searchTimeout.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/portal/places/autocomplete?input=${encodeURIComponent(value)}`, getPortalToken());
          setAddressSuggestions(res.data?.predictions || []);
          setShowSuggestions(true);
        } catch { setAddressSuggestions([]); }
      }, 300);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (desc) => {
    setForm({ ...form, address: desc });
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  const urgencyOptions = [
    { value: "normal", label: "Normal", desc: "Within a few days", icon: Clock, color: "#3B82F6" },
    { value: "urgent", label: "Urgent", desc: "Within 24 hours", icon: AlertTriangle, color: "#F59E0B" },
    { value: "emergency", label: "Emergency", desc: "ASAP", icon: Zap, color: "#EF4444" },
  ];

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 rounded-2xl bg-white border-0 shadow-2xl" data-testid="book-job-modal">
        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-1">Request Submitted!</h3>
            <p className="text-sm text-slate-400 mb-6">
              We've received your service request and will get back to you shortly.
            </p>
            <Button onClick={handleDone} className="bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl px-6" data-testid="book-done-btn">
              View My Jobs
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="p-5 pb-0">
              <DialogTitle className="text-lg font-semibold text-slate-800 font-['Outfit']">
                {step === 1 ? "Book a Service" : "Schedule & Details"}
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-400">
                {step === 1 ? "What service do you need?" : "When and where do you need it?"}
              </DialogDescription>
              {/* Step indicators */}
              <div className="flex gap-1.5 pt-3">
                <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 1 ? "bg-[#0046BE]" : "bg-slate-100"}`} />
                <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 2 ? "bg-[#0046BE]" : "bg-slate-100"}`} />
              </div>
            </DialogHeader>

            <div className="p-5 space-y-4">
              {step === 1 ? (
                <>
                  {/* Service Type Selection */}
                  {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#0046BE]" /></div>
                  ) : services.length > 0 ? (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">Service Type</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {services.map((svc) => (
                          <button
                            key={svc.service_id}
                            type="button"
                            onClick={() => setForm({ ...form, service_type: svc.name })}
                            data-testid={`service-type-${svc.service_id}`}
                            className={`p-3 rounded-xl text-left text-sm font-medium transition-all border ${
                              form.service_type === svc.name
                                ? "bg-[#0046BE]/[0.06] border-[#0046BE]/30 text-[#0046BE]"
                                : "bg-white border-slate-100 text-slate-600 hover:border-slate-200"
                            }`}
                          >
                            {svc.name}
                            {svc.default_price > 0 && (
                              <span className="block text-xs text-slate-300 mt-0.5">From ${svc.default_price}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Urgency */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Priority</Label>
                    <div className="flex gap-2">
                      {urgencyOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setForm({ ...form, urgency: opt.value })}
                          data-testid={`urgency-${opt.value}`}
                          className={`flex-1 p-2.5 rounded-xl text-center transition-all border ${
                            form.urgency === opt.value
                              ? "border-current shadow-sm"
                              : "border-slate-100 hover:border-slate-200"
                          }`}
                          style={form.urgency === opt.value ? { color: opt.color, backgroundColor: `${opt.color}08`, borderColor: `${opt.color}30` } : {}}
                        >
                          <opt.icon className="w-4 h-4 mx-auto mb-1" style={form.urgency === opt.value ? { color: opt.color } : { color: "#CBD5E1" }} />
                          <span className={`text-xs font-medium ${form.urgency === opt.value ? "" : "text-slate-500"}`}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Describe what you need *</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="e.g., Need locks changed on front and back door..."
                      className="min-h-[100px] resize-none rounded-xl border-slate-100 focus:border-[#0046BE] text-sm"
                      data-testid="book-description"
                    />
                  </div>

                  <Button
                    onClick={() => { if (!form.description.trim()) { toast.error("Please describe the service you need"); return; } setStep(2); }}
                    className="w-full bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl py-5"
                    data-testid="book-next-btn"
                  >
                    Next
                    <ChevronDown className="w-4 h-4 ml-1 rotate-[-90deg]" />
                  </Button>
                </>
              ) : (
                <>
                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">Preferred Date</Label>
                      <Input
                        type="date"
                        min={today}
                        value={form.preferred_date}
                        onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                        className="rounded-xl border-slate-100 focus:border-[#0046BE] text-sm"
                        data-testid="book-date"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">Preferred Time</Label>
                      <Input
                        type="time"
                        value={form.preferred_time}
                        onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                        className="rounded-xl border-slate-100 focus:border-[#0046BE] text-sm"
                        data-testid="book-time"
                      />
                    </div>
                  </div>

                  {/* Address — with saved addresses picker */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Service Address</Label>
                    {savedAddresses.length > 0 && !form.address && (
                      <div className="space-y-1.5 mb-2">
                        {savedAddresses.map((addr) => (
                          <button
                            key={addr.address_id}
                            type="button"
                            onClick={() => setForm({ ...form, address: addr.full_address })}
                            className={`w-full text-left p-2.5 rounded-xl border transition-all flex items-center gap-2.5 ${
                              form.address === addr.full_address
                                ? "border-[#0046BE]/30 bg-[#0046BE]/[0.04]"
                                : "border-slate-100 hover:border-slate-200"
                            }`}
                            data-testid={`pick-saved-addr-${addr.address_id}`}
                          >
                            <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-600">{addr.label}{addr.is_default ? " (Default)" : ""}</p>
                              <p className="text-[11px] text-slate-400 truncate">{addr.full_address}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="relative">
                      <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 z-10" />
                      <Input
                        value={form.address}
                        onChange={(e) => handleAddressChange(e.target.value)}
                        onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        placeholder={savedAddresses.length > 0 ? "Or type a new address..." : "Enter your address"}
                        className="pl-9 rounded-xl border-slate-100 focus:border-[#0046BE] text-sm"
                        data-testid="book-address"
                      />
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <div
                          className="absolute z-[9999] left-0 right-0 top-full mt-1 bg-white border border-slate-100 rounded-xl shadow-lg max-h-48 overflow-auto overscroll-contain"
                          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                          data-testid="address-suggestions"
                        >
                          {addressSuggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onMouseDown={() => selectSuggestion(s.description)}
                              className="w-full text-left px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                            >
                              <MapPin className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                              <span className="truncate">{s.description}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-50 rounded-xl p-3.5 space-y-2">
                    <p className="text-xs font-medium text-slate-500">Summary</p>
                    {form.service_type && <p className="text-sm text-slate-700"><span className="text-slate-400">Service:</span> {form.service_type}</p>}
                    <p className="text-sm text-slate-700"><span className="text-slate-400">Priority:</span> {form.urgency.charAt(0).toUpperCase() + form.urgency.slice(1)}</p>
                    <p className="text-sm text-slate-600 line-clamp-2">{form.description}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl border-slate-100 text-slate-500" data-testid="book-back-btn">
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="flex-[2] bg-[#0046BE] hover:bg-[#0036A0] text-white rounded-xl py-5"
                      data-testid="book-submit-btn"
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Request"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ======================== FAB TAB BAR ========================

const PortalFABTab = () => {
  const [bookOpen, setBookOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { path: "/portal", icon: Home, label: "Home", exact: true },
    { path: "/portal/jobs", icon: Briefcase, label: "Jobs" },
    { key: "book", icon: Plus, label: "Book", isFab: true },
    { path: "/portal/addresses", icon: MapPin, label: "Addresses" },
    { path: "/portal/settings", icon: Calendar, label: "More" },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Bottom Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden" data-testid="portal-fab-tab">
        <div className="bg-white border-t border-slate-100 px-2 pb-[env(safe-area-inset-bottom)]" style={{ boxShadow: "0 -2px 12px rgba(0,0,0,0.04)" }}>
          <div className="flex items-end justify-around max-w-md mx-auto">
            {tabs.map((tab) => {
              if (tab.isFab) {
                return (
                  <button
                    key="book-fab"
                    onClick={() => setBookOpen(true)}
                    className="relative -mt-5 flex flex-col items-center"
                    data-testid="portal-fab-book-btn"
                  >
                    <div className="w-[52px] h-[52px] rounded-2xl flex items-center justify-center shadow-lg transition-transform active:scale-95"
                      style={{ background: "linear-gradient(135deg, #0046BE 0%, #0055E6 100%)" }}
                    >
                      <Plus className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] font-medium text-[#0046BE] mt-1 font-['Outfit']">Book</span>
                  </button>
                );
              }

              const active = isActive(tab.path, tab.exact);
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className="flex flex-col items-center py-2 px-3 min-w-[56px] transition-colors"
                  data-testid={`portal-tab-${tab.label.toLowerCase()}`}
                >
                  <tab.icon className={`w-5 h-5 ${active ? "text-[#0046BE]" : "text-slate-300"}`} />
                  <span className={`text-[10px] mt-0.5 font-medium font-['Outfit'] ${active ? "text-[#0046BE]" : "text-slate-300"}`}>
                    {tab.label}
                  </span>
                  {active && <div className="w-1 h-1 rounded-full bg-[#0046BE] mt-0.5" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop FAB - floating button */}
      <div className="hidden lg:block fixed bottom-8 right-8 z-50">
        <button
          onClick={() => setBookOpen(true)}
          className="flex items-center gap-2 px-5 py-3 rounded-2xl text-white font-medium text-sm shadow-xl transition-all hover:shadow-2xl hover:scale-[1.02] active:scale-95 font-['Outfit']"
          style={{ background: "linear-gradient(135deg, #0046BE 0%, #0055E6 100%)" }}
          data-testid="portal-fab-desktop"
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
          Book a Service
        </button>
      </div>

      <BookJobModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </>
  );
};

export default PortalFABTab;
